<template>
  <div class="tender-analytics-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="analytics" color="deep-purple" class="q-mr-sm" />
            Tender Analytics
          </div>
          <div class="panel-subtitle">
            Performance insights and data analysis for this tender
          </div>
        </div>
        <div class="col-auto">
          <q-btn-group>
            <q-btn
              flat
              icon="refresh"
              color="grey-7"
              @click="refreshAnalytics"
            >
              <q-tooltip>Refresh Data</q-tooltip>
            </q-btn>
            <q-btn
              flat
              icon="download"
              color="grey-7"
              @click="exportAnalytics"
            >
              <q-tooltip>Export Report</q-tooltip>
            </q-btn>
          </q-btn-group>
        </div>
      </div>
    </div>

    <!-- Key Performance Indicators -->
    <div class="kpi-section q-mb-lg">
      <div class="section-title">
        <q-icon name="dashboard" color="primary" class="q-mr-sm" />
        Key Performance Indicators
      </div>
      
      <div class="row q-col-gutter-md">
        <!-- Response Rate -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="kpi-card">
            <q-card-section class="text-center">
              <q-circular-progress
                :value="responseRate / 100"
                color="positive"
                size="60px"
                :thickness="0.15"
                class="q-mb-sm"
              >
                <div class="kpi-value">{{ responseRate }}%</div>
              </q-circular-progress>
              <div class="kpi-label">Response Rate</div>
              <div class="kpi-sublabel">{{ respondedContractors }}/{{ totalContractors }} responded</div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Average Quote Value -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="kpi-card">
            <q-card-section class="text-center">
              <q-icon name="attach_money" color="secondary" size="40px" class="q-mb-sm" />
              <div class="kpi-value">${{ formatCurrency(averageQuoteValue) }}</div>
              <div class="kpi-label">Avg Quote Value</div>
              <div class="kpi-sublabel">{{ quotesReceived }} quotes received</div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Time to Respond -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="kpi-card">
            <q-card-section class="text-center">
              <q-icon name="schedule" color="warning" size="40px" class="q-mb-sm" />
              <div class="kpi-value">{{ averageResponseTime }}</div>
              <div class="kpi-label">Avg Response Time</div>
              <div class="kpi-sublabel">Days to quote submission</div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Competition Index -->
        <div class="col-12 col-md-3">
          <q-card flat bordered class="kpi-card">
            <q-card-section class="text-center">
              <q-icon name="trending_up" color="accent" size="40px" class="q-mb-sm" />
              <div class="kpi-value">{{ competitionIndex }}</div>
              <div class="kpi-label">Competition Index</div>
              <div class="kpi-sublabel">Market competitiveness</div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="charts-section">
      <div class="row q-col-gutter-lg">
        <!-- Quote Value Distribution -->
        <div class="col-12 col-lg-6">
          <q-card flat bordered class="chart-card">
            <q-card-section>
              <div class="chart-header">
                <div class="chart-title">Quote Value Distribution</div>
                <div class="chart-subtitle">Range of submitted quotes</div>
              </div>
              
              <div class="chart-container">
                <div v-if="quoteValues.length === 0" class="no-data">
                  <q-icon name="bar_chart" size="3em" color="grey-5" />
                  <div class="no-data-text">No quote data available</div>
                </div>
                
                <div v-else class="quote-chart">
                  <div
                    v-for="(quote, index) in quoteValues"
                    :key="index"
                    class="quote-bar"
                  >
                    <div class="bar-container">
                      <div
                        class="bar"
                        :style="{ 
                          height: `${(quote.value / maxQuoteValue) * 100}%`,
                          backgroundColor: getQuoteBarColor(quote.value)
                        }"
                      />
                    </div>
                    <div class="bar-label">{{ quote.contractor }}</div>
                    <div class="bar-value">${{ formatCurrency(quote.value) }}</div>
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Timeline Analysis -->
        <div class="col-12 col-lg-6">
          <q-card flat bordered class="chart-card">
            <q-card-section>
              <div class="chart-header">
                <div class="chart-title">Response Timeline</div>
                <div class="chart-subtitle">When contractors responded</div>
              </div>
              
              <div class="chart-container">
                <div v-if="responseTimeline.length === 0" class="no-data">
                  <q-icon name="timeline" size="3em" color="grey-5" />
                  <div class="no-data-text">No response data available</div>
                </div>
                
                <div v-else class="timeline-chart">
                  <div
                    v-for="(response, index) in responseTimeline"
                    :key="index"
                    class="timeline-item"
                  >
                    <div class="timeline-date">Day {{ response.day }}</div>
                    <div class="timeline-bar-container">
                      <div
                        class="timeline-bar"
                        :style="{ width: `${(response.count / maxResponseCount) * 100}%` }"
                      />
                    </div>
                    <div class="timeline-count">{{ response.count }} response{{ response.count !== 1 ? 's' : '' }}</div>
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>

    <!-- Detailed Analysis -->
    <div class="detailed-analysis q-mt-lg">
      <div class="row q-col-gutter-lg">
        <!-- Contractor Performance -->
        <div class="col-12 col-lg-8">
          <q-card flat bordered class="analysis-card">
            <q-card-section>
              <div class="analysis-header">
                <div class="analysis-title">Contractor Performance Analysis</div>
                <div class="analysis-subtitle">Detailed breakdown by contractor</div>
              </div>
              
              <q-table
                :rows="contractorAnalysis"
                :columns="contractorColumns"
                row-key="id"
                :pagination="{ rowsPerPage: 0 }"
                flat
                class="contractor-table"
              >
                <template #body-cell-performance="props">
                  <q-td :props="props">
                    <q-linear-progress
                      :value="props.value / 100"
                      :color="getPerformanceColor(props.value)"
                      size="8px"
                      rounded
                    />
                    <div class="text-caption">{{ props.value }}%</div>
                  </q-td>
                </template>
                
                <template #body-cell-status="props">
                  <q-td :props="props">
                    <q-badge
                      :color="getStatusColor(props.value)"
                      :label="props.value"
                    />
                  </q-td>
                </template>
              </q-table>
            </q-card-section>
          </q-card>
        </div>

        <!-- Summary Insights -->
        <div class="col-12 col-lg-4">
          <q-card flat bordered class="insights-card">
            <q-card-section>
              <div class="insights-header">
                <div class="insights-title">Key Insights</div>
                <div class="insights-subtitle">Data-driven recommendations</div>
              </div>
              
              <div class="insights-list">
                <div
                  v-for="insight in insights"
                  :key="insight.id"
                  class="insight-item"
                >
                  <q-icon
                    :name="insight.icon"
                    :color="insight.color"
                    size="20px"
                    class="insight-icon"
                  />
                  <div class="insight-content">
                    <div class="insight-text">{{ insight.text }}</div>
                    <div v-if="insight.action" class="insight-action">
                      <q-btn
                        flat
                        size="sm"
                        :label="insight.action.label"
                        :color="insight.color"
                        @click="executeInsightAction(insight.action.type)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    tender: {
      type: Object,
      required: true,
    },
    contractors: {
      type: Array,
      default: () => [],
    },
    quotes: {
      type: Array,
      default: () => [],
    },
  });

  const $q = useQuasar();

  // Mock data for analytics
  const mockContractors = ref([
    { id: 1, name: 'ABC HVAC Services', status: 'submitted', quote_value: 148500, response_time: 3 },
    { id: 2, name: 'XYZ Mechanical', status: 'submitted', quote_value: 152000, response_time: 5 },
    { id: 3, name: 'Cool Air Solutions', status: 'pending', quote_value: null, response_time: null },
    { id: 4, name: 'ProTemp Systems', status: 'submitted', quote_value: 145000, response_time: 2 },
  ]);

  // Computed properties
  const totalContractors = computed(() => {
    return props.contractors.length || mockContractors.value.length;
  });

  const respondedContractors = computed(() => {
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    return contractors.filter((c) => c.status === 'submitted').length;
  });

  const responseRate = computed(() => {
    if (totalContractors.value === 0) {return 0;}
    return Math.round((respondedContractors.value / totalContractors.value) * 100);
  });

  const quotesReceived = computed(() => {
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    return contractors.filter((c) => c.quote_value).length;
  });

  const averageQuoteValue = computed(() => {
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    const quotesWithValues = contractors.filter((c) => c.quote_value);
    if (quotesWithValues.length === 0) {return 0;}
  
    const total = quotesWithValues.reduce((sum, c) => sum + c.quote_value, 0);
    return total / quotesWithValues.length;
  });

  const averageResponseTime = computed(() => {
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    const respondedContractors = contractors.filter((c) => c.response_time);
    if (respondedContractors.length === 0) {return 'N/A';}
  
    const total = respondedContractors.reduce((sum, c) => sum + c.response_time, 0);
    return Math.round(total / respondedContractors.length) + ' days';
  });

  const competitionIndex = computed(() => {
    // Simple competition index based on number of contractors and quote variance
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    const quotesWithValues = contractors.filter((c) => c.quote_value);
  
    if (quotesWithValues.length < 2) {return 'Low';}
  
    const values = quotesWithValues.map((c) => c.quote_value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const variance = ((max - min) / min) * 100;
  
    if (variance > 15) {return 'High';}
    if (variance > 8) {return 'Medium';}
    return 'Low';
  });

  const quoteValues = computed(() => {
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    return contractors
      .filter((c) => c.quote_value)
      .map((c) => ({
        contractor: c.name,
        value: c.quote_value,
      }))
      .sort((a, b) => a.value - b.value);
  });

  const maxQuoteValue = computed(() => {
    if (quoteValues.value.length === 0) {return 0;}
    return Math.max(...quoteValues.value.map((q) => q.value));
  });

  const responseTimeline = computed(() => {
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    const responded = contractors.filter((c) => c.response_time);
  
    const timeline = {};
    responded.forEach((c) => {
      const day = c.response_time;
      timeline[day] = (timeline[day] || 0) + 1;
    });
  
    return Object.entries(timeline)
      .map(([day, count]) => ({ day: parseInt(day), count }))
      .sort((a, b) => a.day - b.day);
  });

  const maxResponseCount = computed(() => {
    if (responseTimeline.value.length === 0) {return 0;}
    return Math.max(...responseTimeline.value.map((r) => r.count));
  });

  const contractorAnalysis = computed(() => {
    const contractors = props.contractors.length > 0 ? props.contractors : mockContractors.value;
    return contractors.map((contractor) => ({
      id: contractor.id,
      name: contractor.name,
      status: contractor.status,
      quote_value: contractor.quote_value || 0,
      response_time: contractor.response_time || 'N/A',
      performance: calculatePerformanceScore(contractor),
    }));
  });

  const insights = computed(() => {
    const insights = [];
  
    if (responseRate.value < 50) {
      insights.push({
        id: 1,
        icon: 'warning',
        color: 'warning',
        text: 'Low response rate detected. Consider following up with contractors.',
        action: { label: 'Send Reminder', type: 'send_reminder' },
      });
    }
  
    if (quotesReceived.value > 0) {
      const lowestQuote = Math.min(...quoteValues.value.map((q) => q.value));
      const savings = averageQuoteValue.value - lowestQuote;
      if (savings > 5000) {
        insights.push({
          id: 2,
          icon: 'savings',
          color: 'positive',
          text: `Potential savings of $${formatCurrency(savings)} with lowest quote.`,
        });
      }
    }
  
    if (averageResponseTime.value !== 'N/A' && parseInt(averageResponseTime.value) > 5) {
      insights.push({
        id: 3,
        icon: 'schedule',
        color: 'info',
        text: 'Response times are longer than average. Consider extending deadline.',
        action: { label: 'Extend Deadline', type: 'extend_deadline' },
      });
    }
  
    return insights;
  });

  // Table columns
  const contractorColumns = [
    {
      name: 'name',
      label: 'Contractor',
      field: 'name',
      align: 'left',
      sortable: true,
    },
    {
      name: 'status',
      label: 'Status',
      field: 'status',
      align: 'center',
      sortable: true,
    },
    {
      name: 'quote_value',
      label: 'Quote Value',
      field: 'quote_value',
      align: 'right',
      sortable: true,
      format: (val) => val ? `$${formatCurrency(val)}` : 'N/A',
    },
    {
      name: 'response_time',
      label: 'Response Time',
      field: 'response_time',
      align: 'center',
      sortable: true,
      format: (val) => val ? `${val} days` : 'N/A',
    },
    {
      name: 'performance',
      label: 'Performance',
      field: 'performance',
      align: 'center',
      sortable: true,
    },
  ];

  // Methods
  function formatCurrency(amount) {
    return Number(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  function getQuoteBarColor(value) {
    const ratio = value / maxQuoteValue.value;
    if (ratio < 0.8) {return '#4caf50';} // Green for lower quotes
    if (ratio < 0.9) {return '#ff9800';} // Orange for medium quotes
    return '#f44336'; // Red for higher quotes
  }

  function calculatePerformanceScore(contractor) {
    let score = 0;
  
    // Status score
    if (contractor.status === 'submitted') {score += 40;}
    else if (contractor.status === 'pending') {score += 20;}
  
    // Quote competitiveness score
    if (contractor.quote_value && quoteValues.value.length > 0) {
      const minValue = Math.min(...quoteValues.value.map((q) => q.value));
      const ratio = contractor.quote_value / minValue;
      if (ratio <= 1.05) {score += 30;} // Within 5% of lowest
      else if (ratio <= 1.10) {score += 20;} // Within 10% of lowest
      else if (ratio <= 1.15) {score += 10;} // Within 15% of lowest
    }
  
    // Response time score
    if (contractor.response_time) {
      if (contractor.response_time <= 2) {score += 30;}
      else if (contractor.response_time <= 4) {score += 20;}
      else if (contractor.response_time <= 7) {score += 10;}
    }
  
    return Math.min(score, 100);
  }

  function getPerformanceColor(score) {
    if (score >= 80) {return 'positive';}
    if (score >= 60) {return 'warning';}
    return 'negative';
  }

  function getStatusColor(status) {
    const colors = {
      submitted: 'positive',
      pending: 'warning',
      declined: 'negative',
    };
    return colors[status] || 'grey';
  }

  function refreshAnalytics() {
    $q.notify({
      color: 'info',
      message: 'Analytics data refreshed',
      timeout: 2000,
    });
  }

  function exportAnalytics() {
    $q.notify({
      color: 'info',
      message: 'Analytics report export functionality will be implemented',
      timeout: 3000,
    });
  }

  function executeInsightAction(actionType) {
    switch (actionType) {
      case 'send_reminder':
        $q.notify({
          color: 'info',
          message: 'Reminder functionality will be implemented',
          timeout: 3000,
        });
        break;
      case 'extend_deadline':
        $q.notify({
          color: 'info',
          message: 'Deadline extension functionality will be implemented',
          timeout: 3000,
        });
        break;
      default:
        $q.notify({
          color: 'info',
          message: 'Action functionality will be implemented',
          timeout: 3000,
        });
    }
  }

  onMounted(() => {
  // Initialize analytics data
  });
</script>

<style lang="scss" scoped>
.tender-analytics-panel {
  .panel-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
    
    .panel-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      color: #666;
      font-size: 0.9rem;
    }
  }
  
  .section-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
  }
  
  .kpi-section {
    .kpi-card {
      height: 140px;
      
      .kpi-value {
        font-size: 1.2rem;
        font-weight: 700;
        color: #333;
      }
      
      .kpi-label {
        font-size: 0.9rem;
        font-weight: 600;
        color: #666;
        margin-bottom: 4px;
      }
      
      .kpi-sublabel {
        font-size: 0.8rem;
        color: #999;
      }
    }
  }
  
  .chart-card,
  .analysis-card,
  .insights-card {
    height: 400px;
    
    .chart-header,
    .analysis-header,
    .insights-header {
      margin-bottom: 16px;
      
      .chart-title,
      .analysis-title,
      .insights-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #333;
      }
      
      .chart-subtitle,
      .analysis-subtitle,
      .insights-subtitle {
        font-size: 0.85rem;
        color: #666;
      }
    }
    
    .chart-container {
      height: 280px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .no-data {
        text-align: center;
        color: #666;
        
        .no-data-text {
          margin-top: 8px;
          font-size: 0.9rem;
        }
      }
      
      .quote-chart {
        display: flex;
        align-items: end;
        justify-content: space-around;
        height: 100%;
        width: 100%;
        padding: 0 20px;
        
        .quote-bar {
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
          min-width: 80px;
          
          .bar-container {
            flex: 1;
            display: flex;
            align-items: end;
            width: 40px;
            margin-bottom: 8px;
            
            .bar {
              width: 100%;
              min-height: 20px;
              border-radius: 4px 4px 0 0;
              transition: all 0.3s ease;
            }
          }
          
          .bar-label {
            font-size: 0.8rem;
            font-weight: 500;
            text-align: center;
            margin-bottom: 4px;
          }
          
          .bar-value {
            font-size: 0.75rem;
            color: #666;
            text-align: center;
          }
        }
      }
      
      .timeline-chart {
        width: 100%;
        
        .timeline-item {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
          
          .timeline-date {
            width: 60px;
            font-size: 0.8rem;
            font-weight: 500;
          }
          
          .timeline-bar-container {
            flex: 1;
            height: 20px;
            background: #f0f0f0;
            border-radius: 10px;
            margin: 0 12px;
            overflow: hidden;
            
            .timeline-bar {
              height: 100%;
              background: linear-gradient(90deg, #1976d2, #42a5f5);
              border-radius: 10px;
              transition: width 0.3s ease;
            }
          }
          
          .timeline-count {
            width: 80px;
            font-size: 0.8rem;
            color: #666;
            text-align: right;
          }
        }
      }
    }
  }
  
  .contractor-table {
    :deep(.q-table) {
      .q-td {
        padding: 8px 16px;
      }
    }
  }
  
  .insights-list {
    .insight-item {
      display: flex;
      align-items: flex-start;
      margin-bottom: 16px;
      padding: 12px;
      background: #f8f9fa;
      border-radius: 8px;
      
      .insight-icon {
        margin-right: 12px;
        margin-top: 2px;
      }
      
      .insight-content {
        flex: 1;
        
        .insight-text {
          font-size: 0.9rem;
          line-height: 1.4;
          margin-bottom: 8px;
        }
        
        .insight-action {
          text-align: right;
        }
      }
    }
  }
}
</style>